import React from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import AddCenterForm from "../TournamentLocations/AddCenterForm";
import SquadDateTrigger from "./SquadDateTrigger";
import {
  AddTimeTrigger,
  AlsoEventChip,
  SuggestedTimeChip,
  TimeChip,
} from "./SquadTimeChips";
import {
  alsoEventIdsForTime,
  suggestedTimesFromEvents,
} from "../../utility/bulkSquadTree";

const HouseSquadBlock = ({
  block,
  orgHouses,
  title,
  canRemove,
  showAddCenter,
  showAddCenterForm,
  onToggleAddCenter,
  organizationId,
  isLoggedIn,
  onCenterAdded,
  onChangeLocation,
  onRemoveHouse,
  squadsToCreate,
  saving,
  onChangeDate,
  onAddTime,
  onRemoveTime,
  onAddDay,
  onRemoveDay,
  onToggleAlsoEvent,
}) => (
  <section>
    <div className="mb-4">
      {orgHouses.length === 1 && block.locationId ? (
        <h3 className="heading-display text-lg mb-3">{title}</h3>
      ) : (
        <label className="block mb-3">
          <span className="block text-sm font-medium text-brand-charcoal mb-2">
            Bowling center
          </span>
          <select
            value={block.locationId}
            onChange={(event) => onChangeLocation(event.target.value)}
            className="w-full p-3 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
          >
            <option value="">Select a bowling center</option>
            {orgHouses.map((house) => (
              <option
                key={house.bowlingCenters.id}
                value={house.bowlingCenters.id}
              >
                {house.bowlingCenters.centerName}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="space-y-3">
        {showAddCenter ? (
          <div
            className="w-full"
            onKeyDown={(keyEvent) => {
              if (keyEvent.key === "Enter") keyEvent.preventDefault();
            }}
          >
            <AddCenterForm
              showForm={showAddCenterForm}
              onToggleForm={onToggleAddCenter}
              organizationId={organizationId}
              hideHeading
              embedded
              onCenterAdded={onCenterAdded}
              isLoggedIn={isLoggedIn}
              canManage
            />
          </div>
        ) : null}
        {canRemove ? (
          <button
            type="button"
            onClick={onRemoveHouse}
            title="Remove this house"
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-brand-muted hover:bg-red-50 hover:text-red-600"
          >
            <FaTimes className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
    </div>

    <div className="space-y-6">
      {block.events.map((eventItem) => {
        const eventSquadCount = squadsToCreate.filter((squad) => {
          const eventIds = squad.eventIds || [squad.eventId];
          return (
            eventIds.includes(eventItem.eventId) &&
            squad.locationId === block.locationId
          );
        }).length;
        const alsoEvents = block.events.filter(
          (other) => other.eventId !== eventItem.eventId
        );
        return (
          <div key={eventItem.eventId}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h4 className="heading-display text-lg">{eventItem.eventName}</h4>
              <span className="text-sm text-brand-muted">
                {eventSquadCount === 0
                  ? "No times yet"
                  : `${eventSquadCount} squad${
                      eventSquadCount === 1 ? "" : "s"
                    }`}
              </span>
            </div>
            <div className="card-inset divide-y divide-brand-charcoal/10">
              {eventItem.days.map((day, dayIndex) => (
                <div
                  key={dayIndex}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center"
                >
                  <SquadDateTrigger
                    value={day.date}
                    disabled={saving}
                    onChange={(date) =>
                      onChangeDate(eventItem.eventId, dayIndex, date)
                    }
                  />
                  <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                    {day.times.map((time) => {
                      const alsoIds = alsoEventIdsForTime(day, time);
                      return (
                        <span
                          key={time}
                          className="inline-flex flex-wrap items-center gap-1"
                        >
                          <TimeChip
                            time={time}
                            onRemove={() =>
                              onRemoveTime(eventItem.eventId, dayIndex, time)
                            }
                          />
                          {alsoEvents.map((other) => (
                            <AlsoEventChip
                              key={`${time}-${other.eventId}`}
                              eventName={other.eventName}
                              checked={alsoIds.includes(other.eventId)}
                              disabled={saving}
                              onToggle={() =>
                                onToggleAlsoEvent?.(
                                  eventItem.eventId,
                                  dayIndex,
                                  time,
                                  other.eventId
                                )
                              }
                            />
                          ))}
                        </span>
                      );
                    })}
                    {suggestedTimesFromEvents(block.events, day.times).map(
                      (time) => (
                        <SuggestedTimeChip
                          key={`suggest-${time}`}
                          time={time}
                          disabled={saving}
                          onAdd={(nextTime) =>
                            onAddTime(eventItem.eventId, dayIndex, nextTime)
                          }
                        />
                      )
                    )}
                    <AddTimeTrigger
                      exclude={day.times}
                      disabled={saving}
                      onAdd={(time) =>
                        onAddTime(eventItem.eventId, dayIndex, time)
                      }
                    />
                  </div>
                  {eventItem.days.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => onRemoveDay(eventItem.eventId, dayIndex)}
                      title="Remove this day"
                      className="self-end sm:self-center inline-flex items-center justify-center w-8 h-8 rounded-lg text-brand-muted hover:bg-red-50 hover:text-red-600"
                    >
                      <FaTimes className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                onClick={() => onAddDay(eventItem.eventId)}
                className="flex w-full items-center gap-2 px-4 py-3 text-sm text-brand-muted hover:bg-brand-cream/60 hover:text-brand-charcoal"
              >
                <FaPlus className="h-3 w-3" />
                Add day
              </button>
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

export default HouseSquadBlock;
